// AGC: Centralized API fetch wrapper with Firebase Auth token injection
// AGC: All backend calls should use this instead of raw fetch() with manual token handling
// AGC: Offline-aware — throws OfflineError immediately when navigator.onLine is false
import { auth } from '../lib/firebase'
import { getBackendUrl } from '../utils/backendUrl'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// AGC: Specific error for offline state — consumers can check instanceof to show offline UI
export class OfflineError extends Error {
  constructor() {
    super('Device is offline')
    this.name = 'OfflineError'
  }
}

// AGC: Get current user's ID token — getIdToken(false) returns cached token if still valid,
// AGC: or silently refreshes it if expired. DO NOT cache tokens manually.
// AGC: Wrapped in try-catch so expired token + offline doesn't crash the caller.
async function getToken(): Promise<string | null> {
  try {
    const user = auth.currentUser
    if (!user) return null
    return await user.getIdToken(false)
  } catch {
    // AGC: Token refresh failed (likely offline with expired token) — return null
    // AGC: The request will proceed without auth header; backend will 401 and query will retry later
    return null
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  // AGC: Bail immediately if offline — avoids hanging fetch + wasted retries
  if (!navigator.onLine) {
    throw new OfflineError()
  }

  const token = await getToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const baseUrl = getBackendUrl()
  const response = await fetch(`${baseUrl}${path}`, { ...options, headers })

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Unknown error' }))
    const errBody = body as { error?: string; detail?: string }
    const message = errBody.detail
      ? `${errBody.error ?? 'Error'}: ${errBody.detail}`
      : (errBody.error ?? response.statusText)
    throw new ApiError(response.status, message)
  }

  return await response.json() as T
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
}
